import {
  ArrowsClockwiseIcon,
  ArrowsLeftRightIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Text,
  Tooltip,
  VisuallyHidden,
} from "@radix-ui/themes";

import { cn } from "~/v2/utils/cn";
import type { DataTableColumns } from "~/v3-poc/components/DataTable";
import type {
  PositionsWidgetColumnPreset,
  PositionsWidgetRow,
} from "./PositionsWidget.types";
import {
  isOptionPositionRow,
  POSITIONS_UNAVAILABLE_VALUE,
} from "./PositionsWidget.utils";

export type PositionsWidgetColumnId =
  | "actions"
  | "closeQty"
  | "closeRatio"
  | "symbol"
  | "name"
  | "quantity"
  | "startOfDayQty"
  | "availableQty"
  | "currentPrice"
  | "avgEntryPrice"
  | "costBasis"
  | "marketValue"
  | "lastPrice"
  | "unrealizedIntradayPl"
  | "unrealizedIntradayPlpc"
  | "unrealizedPl"
  | "unrealizedPlpc"
  | "pctOfPortfolio"
  | "currency"
  | "todaysTurnover"
  | "todayAvgBuyPrice"
  | "todayAvgSellPrice"
  | "initialMargin"
  | "delta"
  | "gamma"
  | "vega"
  | "theta"
  | "rho"
  | "impliedVolatility"
  | "intrinsicValue"
  | "extrinsicValue"
  | "assetClass"
  | "side"
  | "underlyingSymbol"
  | "expiresAt"
  | "exchange"
  | "assetMarginable"
  | "assetId";

export interface PositionsWidgetColumnOption {
  id: PositionsWidgetColumnId;
  label: string;
}

/** Close qty/ratio draft for one position (ratio is 1–100). */
export type PositionCloseDraft = { qty: number; ratio: number };

export const POSITIONS_WIDGET_COLUMN_OPTIONS = [
  { id: "symbol", label: "Symbol" },
  { id: "name", label: "Name" },
  { id: "quantity", label: "Current Qty" },
  { id: "startOfDayQty", label: "Start of Day Qty" },
  { id: "availableQty", label: "Available Qty" },
  { id: "currentPrice", label: "Current Price" },
  { id: "avgEntryPrice", label: "Avg Entry" },
  { id: "costBasis", label: "Cost Basis" },
  { id: "marketValue", label: "Market Value" },
  { id: "lastPrice", label: "Last Close" },
  { id: "unrealizedIntradayPl", label: "Today's P&L" },
  { id: "unrealizedIntradayPlpc", label: "Today's P&L %" },
  { id: "unrealizedPl", label: "Total P&L" },
  { id: "unrealizedPlpc", label: "Total P&L %" },
  { id: "pctOfPortfolio", label: "% of Portfolio" },
  { id: "currency", label: "Currency" },
  { id: "todaysTurnover", label: "Today's Turnover" },
  { id: "todayAvgBuyPrice", label: "Today's Avg Purchase Price" },
  { id: "todayAvgSellPrice", label: "Today's Avg Sales Price" },
  { id: "initialMargin", label: "Initial Margin" },
  { id: "delta", label: "Delta" },
  { id: "gamma", label: "Gamma" },
  { id: "vega", label: "Vega" },
  { id: "theta", label: "Theta" },
  { id: "rho", label: "Rho" },
  { id: "impliedVolatility", label: "Implied Volatility" },
  { id: "intrinsicValue", label: "Intrinsic Value" },
  { id: "extrinsicValue", label: "Extrinsic Value" },
  { id: "assetClass", label: "Type" },
  { id: "side", label: "Side" },
  { id: "underlyingSymbol", label: "Underlying" },
  { id: "expiresAt", label: "Expiration" },
  { id: "exchange", label: "Exchange" },
  { id: "assetMarginable", label: "Marginable" },
  { id: "assetId", label: "Asset ID" },
  { id: "closeQty", label: "Close Qty" },
  { id: "closeRatio", label: "Closing ratio" },
  { id: "actions", label: "Action" },
] as const satisfies readonly PositionsWidgetColumnOption[];

export const POSITIONS_WIDGET_COLUMN_IDS: readonly PositionsWidgetColumnId[] =
  POSITIONS_WIDGET_COLUMN_OPTIONS.map(({ id }) => id);

/** Everything a preset or the picker may offer; close steppers stay opt-in. */
const SELECTABLE_COLUMN_IDS = POSITIONS_WIDGET_COLUMN_IDS.filter(
  (id) => id !== "closeQty" && id !== "closeRatio",
);

/** Compact out-of-the-box set: symbol, size, prices, P&L, actions. */
export const DEFAULT_POSITIONS_WIDGET_VISIBLE_COLUMN_IDS = [
  "symbol",
  "quantity",
  "currentPrice",
  "avgEntryPrice",
  "marketValue",
  "unrealizedIntradayPl",
  "unrealizedPl",
  "pctOfPortfolio",
  "actions",
] as const satisfies readonly PositionsWidgetColumnId[];

const SUPERVISION_HIDDEN = new Set<PositionsWidgetColumnId>([
  "startOfDayQty",
  "availableQty",
  "lastPrice",
  "avgEntryPrice",
  "costBasis",
  "unrealizedPl",
  "unrealizedPlpc",
  "currency",
  "todaysTurnover",
  "todayAvgBuyPrice",
  "todayAvgSellPrice",
  "initialMargin",
  "underlyingSymbol",
  "expiresAt",
  "exchange",
  "assetMarginable",
  "intrinsicValue",
  "extrinsicValue",
  "delta",
  "gamma",
  "vega",
  "theta",
  "rho",
  "impliedVolatility",
  "assetId",
]);

const MANUAL_HIDDEN = new Set<PositionsWidgetColumnId>([
  "startOfDayQty",
  "currency",
  "todaysTurnover",
  "todayAvgBuyPrice",
  "todayAvgSellPrice",
  "initialMargin",
  "underlyingSymbol",
  "expiresAt",
  "exchange",
  "assetMarginable",
  "intrinsicValue",
  "extrinsicValue",
  "delta",
  "gamma",
  "vega",
  "theta",
  "rho",
  "impliedVolatility",
  "assetId",
]);

const AGENT_HIDDEN = new Set<PositionsWidgetColumnId>([
  ...SUPERVISION_HIDDEN,
  "assetClass",
  "unrealizedIntradayPlpc",
]);

/** Column ids shown for each positions-one column preset. */
export const getPositionsColumnPresetIds = (
  preset: PositionsWidgetColumnPreset,
): readonly PositionsWidgetColumnId[] => {
  if (preset === "default") {
    return DEFAULT_POSITIONS_WIDGET_VISIBLE_COLUMN_IDS;
  }
  const hidden =
    preset === "risk"
      ? new Set<PositionsWidgetColumnId>()
      : preset === "manual"
        ? MANUAL_HIDDEN
        : AGENT_HIDDEN;
  return SELECTABLE_COLUMN_IDS.filter((id) => !hidden.has(id));
};

const POSITIONS_WIDGET_COLUMN_ID_SET = new Set<string>(
  POSITIONS_WIDGET_COLUMN_IDS,
);

export const isPositionsWidgetColumnId = (
  value: unknown,
): value is PositionsWidgetColumnId =>
  typeof value === "string" && POSITIONS_WIDGET_COLUMN_ID_SET.has(value);

export const getPositionsWidgetColumnOptions = (
  _includeActions: boolean = true,
): readonly PositionsWidgetColumnOption[] => POSITIONS_WIDGET_COLUMN_OPTIONS;

export const getPositionsWidgetAvailableColumnIds = (
  includeActions: boolean = true,
): readonly PositionsWidgetColumnId[] =>
  getPositionsWidgetColumnOptions(includeActions).map(({ id }) => id);

interface TextValueProps {
  value: string;
  label: string;
  color?: "gray" | "green" | "red";
  weight?: "medium" | "bold";
  size?: "1" | "2";
  truncate?: boolean;
  numeric?: boolean;
}

const TextValue = ({
  value,
  label,
  color,
  weight,
  size = "1",
  truncate,
  numeric,
}: TextValueProps) =>
  value === POSITIONS_UNAVAILABLE_VALUE ? (
    <Text size={size} color="gray" aria-label={`${label} unavailable`}>
      {POSITIONS_UNAVAILABLE_VALUE}
    </Text>
  ) : (
    <Text
      size={size}
      color={color}
      weight={weight}
      truncate={truncate}
      className={cn(numeric && "atd-numeric")}
    >
      {value}
    </Text>
  );

type PositionsColumn = DataTableColumns<PositionsWidgetRow>[number];
type PositionsColumnMeta = NonNullable<PositionsColumn["meta"]>;

type PositionsRowColorKey =
  | "unrealizedPlColor"
  | "unrealizedPlpcColor"
  | "unrealizedIntradayPlColor"
  | "unrealizedIntradayPlpcColor";

const createTextColumn = (
  accessorKey: PositionsWidgetColumnId,
  header: string,
  label: string,
  meta: PositionsColumnMeta,
  options: {
    weight?: "medium" | "bold";
    color?: "gray";
    colorKey?: PositionsRowColorKey;
    size?: "1" | "2";
    truncate?: boolean;
  } = {},
): PositionsColumn => ({
  id: accessorKey,
  accessorKey,
  header,
  cell: (info) => (
    <TextValue
      value={info.getValue<string>()}
      label={label}
      weight={options.weight}
      size={options.size ?? (meta.numeric ? "2" : "1")}
      truncate={options.truncate}
      numeric={meta.numeric === true}
      color={
        options.colorKey ? info.row.original[options.colorKey] : options.color
      }
    />
  ),
  enableSorting: true,
  meta,
});

export interface PositionsSelectionConfig {
  selectedIds: ReadonlySet<string>;
  onToggle: (positionId: string, selected: boolean) => void;
  onToggleAll?: (selected: boolean) => void;
  allSelected?: boolean;
  someSelected?: boolean;
}

export interface PositionsRowActions {
  onTrade: (row: PositionsWidgetRow) => void;
  onRoll: (row: PositionsWidgetRow) => void;
  onClose: (row: PositionsWidgetRow) => void;
}

export interface PositionsCloseDraftConfig {
  drafts: ReadonlyMap<string, PositionCloseDraft>;
  onQtyChange: (positionId: string, qty: number) => void;
  onRatioChange: (positionId: string, ratio: number) => void;
}

const Stepper = ({
  label,
  value: _value,
  display,
  disabled,
  onDecrement,
  onIncrement,
}: {
  label: string;
  value: number;
  display: string;
  disabled?: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
}) => (
  <Flex
    align="center"
    gap="1"
    className={cn(disabled && "opacity-45")}
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
  >
    <Button
      type="button"
      size="1"
      variant="soft"
      color="gray"
      disabled={disabled}
      aria-label={`Decrease ${label}`}
      onClick={onDecrement}
    >
      −
    </Button>
    <Text size="1" className="atd-numeric" aria-label={label}>
      {display}
    </Text>
    <Button
      type="button"
      size="1"
      variant="soft"
      color="gray"
      disabled={disabled}
      aria-label={`Increase ${label}`}
      onClick={onIncrement}
    >
      +
    </Button>
  </Flex>
);

const SELECT_COLUMN_WIDTH = "2.5rem";

const createSelectionColumn = (
  selection: PositionsSelectionConfig,
): PositionsColumn => ({
  id: "select",
  header: () =>
    selection.onToggleAll ? (
      <div className="data-pos-select-box">
        <Checkbox
          size="1"
          checked={
            selection.allSelected
              ? true
              : selection.someSelected
                ? "indeterminate"
                : false
          }
          aria-label="Select all positions"
          onCheckedChange={(checked) =>
            selection.onToggleAll?.(checked === true)
          }
        />
      </div>
    ) : (
      <VisuallyHidden>Select positions</VisuallyHidden>
    ),
  enableSorting: false,
  cell: ({ row }) => {
    const selected = selection.selectedIds.has(row.original.id);
    return (
      <div className="data-pos-select-box">
        <Checkbox
          size="1"
          checked={selected}
          aria-label={`Select ${row.original.symbol} position`}
          className="data-pos-row-checkbox"
          onClick={(event) => event.stopPropagation()}
          onCheckedChange={(checked) =>
            selection.onToggle(row.original.id, checked === true)
          }
        />
      </div>
    );
  },
  meta: {
    align: "center",
    width: SELECT_COLUMN_WIDTH,
    sticky: "start",
    stickyOffset: "0",
    className: "data-pos-select-cell",
    headerClassName: "data-pos-select-header",
  },
});

const createActionsColumn = (
  actions: PositionsRowActions,
): PositionsColumn => ({
  id: "actions",
  header: () => (
    <Flex align="center" justify="center" className="w-full">
      Action
    </Flex>
  ),
  enableSorting: false,
  cell: ({ row }) => {
    const { symbol } = row.original;
    const showRoll = isOptionPositionRow(row.original);
    return (
      <Flex
        align="center"
        justify="end"
        gap="1"
        wrap="nowrap"
        onClick={(event) => event.stopPropagation()}
      >
        {showRoll ? (
          <Tooltip content="Roll">
            <IconButton
              type="button"
              size="1"
              variant="surface"
              color="gray"
              aria-label={`Roll ${symbol}`}
              onClick={() => actions.onRoll(row.original)}
            >
              <ArrowsClockwiseIcon aria-hidden size={14} />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip content="Trade">
          <IconButton
            type="button"
            size="1"
            variant="surface"
            color="gray"
            aria-label={`Trade ${symbol}`}
            onClick={() => actions.onTrade(row.original)}
          >
            <ArrowsLeftRightIcon aria-hidden size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Close position">
          <IconButton
            type="button"
            size="1"
            variant="surface"
            color="red"
            aria-label={`Close ${symbol}`}
            onClick={() => actions.onClose(row.original)}
          >
            <XCircleIcon aria-hidden size={14} />
          </IconButton>
        </Tooltip>
      </Flex>
    );
  },
  meta: {
    align: "end",
    width: "7rem",
    sticky: "end",
    className: "data-pos-actions-cell",
    headerClassName: "data-pos-actions-cell",
  },
});

const createCloseQtyColumn = (
  closeDrafts: PositionsCloseDraftConfig,
): PositionsColumn => ({
  id: "closeQty",
  header: "Close Qty",
  enableSorting: false,
  cell: ({ row }) => {
    const selected = closeDrafts.drafts.has(row.original.id);
    const draft = closeDrafts.drafts.get(row.original.id);
    const qty = draft?.qty ?? 0;
    return (
      <Stepper
        label={`Close quantity for ${row.original.symbol}`}
        value={qty}
        display={selected ? String(qty) : "—"}
        disabled={!selected}
        onDecrement={() =>
          closeDrafts.onQtyChange(row.original.id, Math.max(0, qty - 1))
        }
        onIncrement={() => closeDrafts.onQtyChange(row.original.id, qty + 1)}
      />
    );
  },
  meta: { align: "start", width: "8rem", numeric: true },
});

const createCloseRatioColumn = (
  closeDrafts: PositionsCloseDraftConfig,
): PositionsColumn => ({
  id: "closeRatio",
  header: "Closing ratio",
  enableSorting: false,
  cell: ({ row }) => {
    const selected = closeDrafts.drafts.has(row.original.id);
    const draft = closeDrafts.drafts.get(row.original.id);
    const ratio = draft?.ratio ?? 100;
    return (
      <Stepper
        label={`Closing ratio for ${row.original.symbol}`}
        value={ratio}
        display={selected ? `${ratio}%` : "—"}
        disabled={!selected}
        onDecrement={() =>
          closeDrafts.onRatioChange(row.original.id, Math.max(1, ratio - 5))
        }
        onIncrement={() =>
          closeDrafts.onRatioChange(row.original.id, Math.min(100, ratio + 5))
        }
      />
    );
  },
  meta: { align: "start", width: "9rem", numeric: true },
});

const NUMERIC = { align: "end" as const, numeric: true };

const POSITIONS_DATA_COLUMNS: DataTableColumns<PositionsWidgetRow> = [
  {
    id: "symbol",
    accessorKey: "symbol",
    header: "Symbol",
    enableSorting: true,
    cell: ({ row }) => {
      const { symbol, compactContract, underlyingSymbol } = row.original;

      if (symbol === POSITIONS_UNAVAILABLE_VALUE) {
        return <TextValue value={symbol} label="Symbol" />;
      }

      if (compactContract !== POSITIONS_UNAVAILABLE_VALUE) {
        const underlying =
          underlyingSymbol === POSITIONS_UNAVAILABLE_VALUE
            ? symbol
            : underlyingSymbol;
        return (
          <Flex align="center" gap="1" wrap="nowrap" title={symbol}>
            <Text size="2">{underlying}</Text>
            <Text size="1" color="gray" className="atd-numeric shrink-0">
              {compactContract}
            </Text>
          </Flex>
        );
      }

      return (
        <Text weight="bold" size="2" truncate title={symbol}>
          {symbol.replace("/", "")}
        </Text>
      );
    },
    meta: {
      width: "11rem",
      sticky: "start",
      stickyOffset: SELECT_COLUMN_WIDTH,
      className: "data-pos-symbol-cell",
      headerClassName: "data-pos-symbol-cell",
    },
  },
  createTextColumn(
    "name",
    "Name",
    "Name",
    { width: "13rem" },
    { size: "2", color: "gray", truncate: true },
  ),
  createTextColumn("quantity", "Current Qty", "Current quantity", {
    ...NUMERIC,
    width: "8rem",
  }),
  createTextColumn(
    "startOfDayQty",
    "Start of Day Qty",
    "Start of day quantity",
    {
      ...NUMERIC,
      width: "9rem",
    },
  ),
  createTextColumn("availableQty", "Available Qty", "Available quantity", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("currentPrice", "Current Price", "Current price", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("avgEntryPrice", "Avg Entry", "Average entry price", {
    ...NUMERIC,
    width: "8rem",
  }),
  createTextColumn("costBasis", "Cost Basis", "Cost basis", {
    ...NUMERIC,
    width: "8rem",
  }),
  createTextColumn("marketValue", "Market Value", "Market value", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("lastPrice", "Last Close", "Last close", {
    ...NUMERIC,
    width: "8rem",
  }),
  createTextColumn(
    "unrealizedIntradayPl",
    "Today's P&L",
    "Today profit and loss",
    { ...NUMERIC, width: "8.5rem" },
    { colorKey: "unrealizedIntradayPlColor", size: "2" },
  ),
  createTextColumn(
    "unrealizedIntradayPlpc",
    "Today's P&L %",
    "Today profit and loss percent",
    { ...NUMERIC, width: "8.5rem" },
    { colorKey: "unrealizedIntradayPlpcColor", size: "2" },
  ),
  createTextColumn(
    "unrealizedPl",
    "Total P&L",
    "Unrealized profit and loss",
    { ...NUMERIC, width: "8.5rem" },
    { colorKey: "unrealizedPlColor", size: "2" },
  ),
  createTextColumn(
    "unrealizedPlpc",
    "Total P&L %",
    "Unrealized profit and loss percent",
    { ...NUMERIC, width: "8.5rem" },
    { colorKey: "unrealizedPlpcColor", size: "2" },
  ),
  createTextColumn("pctOfPortfolio", "% of Portfolio", "Percent of portfolio", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn(
    "currency",
    "Currency",
    "Currency",
    {
      width: "6.5rem",
      numeric: true,
    },
    { size: "2" },
  ),
  createTextColumn("todaysTurnover", "Today's Turnover", "Today's turnover", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn(
    "todayAvgBuyPrice",
    "Today's Avg Purchase Price",
    "Today's average purchase price",
    { ...NUMERIC, width: "11rem" },
  ),
  createTextColumn(
    "todayAvgSellPrice",
    "Today's Avg Sales Price",
    "Today's average sales price",
    { ...NUMERIC, width: "11rem" },
  ),
  createTextColumn("initialMargin", "Initial Margin", "Initial margin", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("delta", "Delta", "Delta", { ...NUMERIC, width: "6.5rem" }),
  createTextColumn("gamma", "Gamma", "Gamma", { ...NUMERIC, width: "6.5rem" }),
  createTextColumn("vega", "Vega", "Vega", { ...NUMERIC, width: "6.5rem" }),
  createTextColumn("theta", "Theta", "Theta", { ...NUMERIC, width: "6.5rem" }),
  createTextColumn("rho", "Rho", "Rho", { ...NUMERIC, width: "6.5rem" }),
  createTextColumn(
    "impliedVolatility",
    "Implied Volatility",
    "Implied volatility",
    { ...NUMERIC, width: "8.5rem" },
  ),
  createTextColumn("intrinsicValue", "Intrinsic Value", "Intrinsic value", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("extrinsicValue", "Extrinsic Value", "Extrinsic value", {
    ...NUMERIC,
    width: "9rem",
  }),
  createTextColumn("assetClass", "Type", "Asset class", { width: "6rem" }),
  {
    id: "side",
    accessorKey: "side",
    header: "Side",
    cell: (info) =>
      info.getValue<string>() === POSITIONS_UNAVAILABLE_VALUE ? (
        <TextValue value={info.getValue<string>()} label="Side" />
      ) : (
        <Badge size="1" color={info.row.original.sideColor} variant="soft">
          {info.getValue<string>()}
        </Badge>
      ),
    enableSorting: true,
    meta: { width: "5rem" },
  },
  createTextColumn("underlyingSymbol", "Underlying", "Underlying symbol", {
    width: "7rem",
  }),
  createTextColumn("expiresAt", "Expiration", "Expiration", {
    ...NUMERIC,
    width: "7rem",
  }),
  createTextColumn("exchange", "Exchange", "Exchange", { width: "6rem" }),
  createTextColumn("assetMarginable", "Marginable", "Asset marginable", {
    width: "6rem",
  }),
  createTextColumn("assetId", "Asset ID", "Asset ID", { width: "12rem" }),
];

export const createPositionsWidgetColumns = (
  actions?: PositionsRowActions,
  selection?: PositionsSelectionConfig,
  closeDrafts?: PositionsCloseDraftConfig,
): DataTableColumns<PositionsWidgetRow> => {
  const dataColumns = POSITIONS_DATA_COLUMNS.flatMap((column) =>
    column.id === "quantity" && closeDrafts
      ? [
          column,
          createCloseQtyColumn(closeDrafts),
          createCloseRatioColumn(closeDrafts),
        ]
      : [column],
  );

  return [
    ...(selection ? [createSelectionColumn(selection)] : []),
    ...dataColumns,
    ...(actions ? [createActionsColumn(actions)] : []),
  ];
};

export const POSITIONS_WIDGET_COLUMNS = createPositionsWidgetColumns();

export const selectVisiblePositionsWidgetColumns = (
  visibleColumnIds: readonly PositionsWidgetColumnId[],
  columns: DataTableColumns<PositionsWidgetRow> = POSITIONS_WIDGET_COLUMNS,
): DataTableColumns<PositionsWidgetRow> => {
  const visibleColumnIdSet = new Set(visibleColumnIds);

  return columns.filter(
    (column) =>
      column.id === "select" ||
      (isPositionsWidgetColumnId(column.id) &&
        visibleColumnIdSet.has(column.id)),
  );
};
